// ============================================================
// جست‌وجوی مکان و تبدیل مختصات به آدرس — با Nominatim
//
// جایگزین سرویس‌های نشان (v1/search و v5/reverse) که کلید می‌خواستند.
// Nominatim سرویس رسمی OpenStreetMap است، رایگان و بی‌کلید، و برای
// ایران آدرس فارسی برمی‌گرداند (accept-language=fa).
//
// قاعده‌ی استفاده‌ی Nominatim دو چیز می‌خواهد که رعایت شده:
//   ۱) حداکثر یک درخواست در ثانیه → جست‌وجو فقط با Enter/دکمه اجرا
//      می‌شود، نه با هر حرفی که تایپ می‌شود.
//   ۲) محدود کردن نتایج به کشور → countrycodes=ir
//
// هیچ خطایی به کاربر به شکل خطای فنی نشان داده نمی‌شود: اگر
// جست‌وجو کار نکرد، کاربر همچنان می‌تواند روی نقشه کلیک کند.
// ============================================================

const BASE = "https://nominatim.openstreetmap.org";
const TIMEOUT_MS = 12000;

export interface PlaceResult {
    title: string;
    address?: string;
    lat: number;
    lng: number;
}

const withTimeout = async (url: string): Promise<Response> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
        return await fetch(url, {
            signal: controller.signal,
            headers: { Accept: "application/json" },
        });
    } finally {
        clearTimeout(timer);
    }
};

/** جست‌وجوی نام مکان یا آدرس. آرایه‌ی خالی یعنی نتیجه‌ای نبود. */
export const searchPlaces = async (term: string): Promise<PlaceResult[]> => {
    const query = String(term || "").trim();
    if (!query) return [];

    const url =
        BASE +
        "/search?format=jsonv2&addressdetails=1&limit=8&countrycodes=ir" +
        "&accept-language=fa&q=" +
        encodeURIComponent(query);

    const res = await withTimeout(url);
    if (!res.ok) throw new Error("search failed: " + res.status);

    const data = await res.json();
    if (!Array.isArray(data)) return [];

    return data
        .map((item: any): PlaceResult => {
            const full = String(item.display_name || "");
            // نام کوتاه = اولین تکه‌ی آدرس؛ بقیه به عنوان توضیح می‌آید
            const parts = full.split(",").map((p: string) => p.trim());
            return {
                title: item.name || parts[0] || full,
                address: parts.slice(1, 4).join("، ") || undefined,
                lat: Number(item.lat),
                lng: Number(item.lon),
            };
        })
        .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
};

/** آدرس فارسیِ یک جفت مختصات. رشته‌ی خالی یعنی آدرسی پیدا نشد. */
export const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    const url =
        BASE +
        "/reverse?format=jsonv2&zoom=18&accept-language=fa" +
        "&lat=" + lat +
        "&lon=" + lng;

    const res = await withTimeout(url);
    if (!res.ok) throw new Error("reverse failed: " + res.status);

    const data = await res.json();
    const full = String(data?.display_name || "").trim();
    if (!full) return "";

    // آدرس کامل Nominatim تا «ایران» ادامه دارد و در یک نوار باریک جا
    // نمی‌شود؛ چهار تکه‌ی اول همان چیزی است که به کار کاربر می‌آید.
    const parts = full.split(",").map((p: string) => p.trim()).filter(Boolean);
    const short = parts.slice(0, 4).join("، ");
    return short.length >= 5 ? short : full;
};
